import { useEffect, useMemo, useState } from "react";
import { Loader2, Share2 } from "lucide-react";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useReviewShares } from "@/hooks/useReviewShares";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/contexts/AuthContext";
import type { ReviewShareUser } from "@/types";

interface OrgMemberOption {
  id: string;
  full_name: string | null;
  email: string;
}

interface ShareReviewDialogProps {
  reviewId: string;
  isOpen: boolean;
  onClose: () => void;
}

function memberLabel(member: OrgMemberOption): string {
  return member.full_name?.trim() || member.email;
}

export function ShareReviewDialog({ reviewId, isOpen, onClose }: ShareReviewDialogProps) {
  const { user } = useAuth();
  const { shares, isLoading, isSaving, error, fetchShares, saveShares } = useReviewShares();
  const [members, setMembers] = useState<OrgMemberOption[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setSearch("");
    setSaveError(null);
    void fetchShares(reviewId);

    const loadMembers = async () => {
      setMembersLoading(true);
      setMembersError(null);
      try {
        const { data, error: listError } = await authClient.organization.listMembers();
        if (listError || !data?.members) {
          throw new Error(listError?.message ?? "Failed to load organization members");
        }

        const options = data.members
          .filter((member) => member.userId !== user?.id)
          .map((member) => ({
            id: member.userId,
            full_name: member.user?.name ?? null,
            email: member.user?.email ?? "",
          }));
        setMembers(options);
      } catch (err) {
        setMembersError(err instanceof Error ? err.message : "Failed to load organization members");
        setMembers([]);
      } finally {
        setMembersLoading(false);
      }
    };

    void loadMembers();
  }, [isOpen, reviewId, fetchShares, user?.id]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedIds(new Set(shares.map((share: ReviewShareUser) => share.id)));
  }, [isOpen, shares]);

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return members;
    return members.filter((member) => {
      const label = memberLabel(member).toLowerCase();
      return label.includes(query) || member.email.toLowerCase().includes(query);
    });
  }, [members, search]);

  const toggleMember = (memberId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaveError(null);
    const success = await saveShares(reviewId, Array.from(selectedIds));
    if (success) {
      onClose();
    } else {
      setSaveError("Failed to save shares");
    }
  };

  const loading = isLoading || membersLoading;
  const displayError = saveError || membersError || error;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSaving && onClose()}>
      <DialogContent className="box-border w-[min(100vw-2rem,36rem)] max-w-xl overflow-hidden">
        <DialogHeader className="min-w-0 space-y-2 pr-6">
          <DialogTitle className="flex items-center gap-2 text-[var(--lm-foreground-strong)] dark:text-slate-100">
            <Share2 className="h-5 w-5 text-[#7c3aed] dark:text-violet-300" />
            Share review
          </DialogTitle>
          <DialogDescription className={`text-[var(--lm-muted)] dark:text-slate-300/85 ${pageDescriptionMobileHiddenClassName}`}>
            Choose organization members who can view this review. Everyone else in your org will not see it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            placeholder="Search members…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          {displayError ? (
            <p className="text-sm text-red-600 dark:text-red-300">{displayError}</p>
          ) : null}

          <div className="max-h-72 overflow-y-auto rounded-[10px] border border-[var(--lm-border-subtle)] bg-white dark:border-indigo-300/15 dark:bg-slate-950/35">
            {loading ? (
              <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-[var(--lm-muted)] dark:text-slate-300">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading members…
              </div>
            ) : filteredMembers.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-[var(--lm-muted)] dark:text-slate-400">
                {members.length === 0
                  ? "No other organization members available"
                  : "No members match your search"}
              </p>
            ) : (
              <ul className="divide-y divide-[var(--lm-border-subtle)] dark:divide-indigo-300/10">
                {filteredMembers.map((member) => {
                  const checked = selectedIds.has(member.id);
                  return (
                    <li key={member.id}>
                      <label className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-[var(--lm-hover)] dark:hover:bg-slate-900/60">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleMember(member.id)}
                          className="mt-0.5"
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-100">
                            {memberLabel(member)}
                          </span>
                          {member.full_name ? (
                            <span className="block truncate text-xs text-[var(--lm-muted)] dark:text-slate-400">
                              {member.email}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <p className="text-xs text-[var(--lm-muted)] dark:text-slate-400">
            {selectedIds.size === 0
              ? "Only you can access this review."
              : `Shared with ${selectedIds.size} member${selectedIds.size === 1 ? "" : "s"}.`}
          </p>
        </div>

        <DialogFooter className="mt-2 w-full min-w-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={isSaving || loading}>
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
